import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, setEnrolmentRequiredHandler } from '@/lib/api/client';
import { REFRESH_LEAD_MS } from '@/lib/config';
import {
  clearSession,
  getClaims,
  hasSessionMarker,
  isAuthenticated,
  setTokens,
  timeUntilExpiry,
} from './session';
import { refreshSession, setRefreshFailureHandler } from '@/lib/api/refresh';
import { isAppError } from '@/lib/api/errors';
import { roleHas, type Permission } from '@/lib/rbac/permissions';
import { applyChromeLocale } from '@/lib/i18n/config';
import type { AuthTokens, Role, User } from '@/lib/api/types';

/**
 * `enrolling`: signed in with an enrolment-only session (D-209) — the account
 * has no 2FA yet, so the console shows the setup screen and nothing else, and
 * every permission check answers no until `verify` returns a full session.
 */
export type AuthStatus = 'loading' | 'authenticated' | 'enrolling' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  role: Role | null;
  can: (permission: Permission) => boolean;
  /** True for translator-style accounts: can translate but not write (spec §6.3). */
  isTranslateOnly: boolean;
  setSession: (tokens: AuthTokens) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /**
   * True while the cold-boot refresh is being retried because the server could
   * not answer — as opposed to refusing. Drives the copy on the boot splash, so
   * a slow or rate-limited backend does not look like a silent hang.
   */
  reconnecting: boolean;
  /** Deep-link to return to after re-auth (spec §3.2). */
  pendingDeepLink: string | null;
  setPendingDeepLink: (path: string | null) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Backoff for a cold boot the server could not answer. Totals ~92s, which
 * outlasts be-svet's fixed 60-second auth window, so a console reloaded into a
 * rate limit recovers by itself instead of showing a sign-in screen to someone
 * who is signed in.
 */
const BOOT_RETRY_MS = [1_000, 3_000, 8_000, 20_000, 30_000, 30_000];

/** True when the server said the credential is no good, rather than "not now". */
function isCredentialRefusal(err: unknown): boolean {
  return isAppError(err) && (err.is('unauthorized') || err.is('forbidden'));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [reconnecting, setReconnecting] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [pendingDeepLink, setPendingDeepLink] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hardLogout = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    clearSession();
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  // Proactive refresh ~60s before expiry; reschedules itself (plan §4.2).
  const scheduleRefresh = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!isAuthenticated()) return;
    const delay = Math.max(0, timeUntilExpiry() - REFRESH_LEAD_MS);
    timerRef.current = setTimeout(() => {
      void refreshSession()
        .then(scheduleRefresh)
        .catch(() => {
          /* failure handler hard-logs out */
        });
    }, delay);
  }, []);

  const refreshUser = useCallback(async () => {
    const me = await api.get<User>('/admin/me');
    setUser(me);
    // The profile carries the operator's interface language and is the source of
    // truth for it. Nothing applied it before, so an account set to Arabic got an
    // English LTR console on every load while its own /admin/me response said
    // otherwise (F-26). Applied here rather than in a screen because it is a
    // property of the session, and every path that establishes one comes through
    // here: cold boot, sign-in, and the account page's own save.
    if (me.locale) applyChromeLocale(me.locale);
  }, []);

  const setSession = useCallback(
    async (tokens: AuthTokens) => {
      setTokens(tokens);
      await refreshUser();
      if (tokens.mfa_enrol) {
        // No refresh cookie stands behind this token, so there is nothing to
        // refresh: when its 15 minutes run out the sign-in starts again.
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(hardLogout, Math.max(0, timeUntilExpiry()));
        setStatus('enrolling');
        return;
      }
      setStatus('authenticated');
      scheduleRefresh();
    },
    [refreshUser, scheduleRefresh, hardLogout],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/admin/auth/logout');
    } catch {
      /* logout is best-effort; always clear locally */
    }
    hardLogout();
  }, [hardLogout]);

  // Wire the refresh-failure handler once.
  useEffect(() => {
    setRefreshFailureHandler(hardLogout);
  }, [hardLogout]);

  // A 403 `mfa_enrolment_required` from anywhere means this session can only
  // set up 2FA; the router then shows the setup screen and nothing else.
  useEffect(() => {
    setEnrolmentRequiredHandler(() => {
      if (isAuthenticated()) setStatus('enrolling');
    });
    return () => setEnrolmentRequiredHandler(null);
  }, []);

  // Bootstrap on mount: attempt a cookie-based refresh to re-establish a session after a
  // hard reload. If it fails, we're simply unauthenticated (no error surfaced).
  //
  // Unless this browser has never signed in, in which case there is nothing to
  // re-establish and the call is skipped. That is not only a saved round trip on
  // first paint: `/auth/refresh` is one of the endpoints be-svet rate-limits at 10
  // per minute per IP, and spending that budget on signed-out visitors is how a
  // shared office address locks its own staff out of the sign-in form. See
  // `hasSessionMarker` in ./session for why the marker is safe to persist.
  useEffect(() => {
    let cancelled = false;
    if (!hasSessionMarker()) {
      setStatus('unauthenticated');
      return;
    }
    (async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          await refreshSession();
          if (cancelled) return;
          await refreshUser();
          if (cancelled) return;
          setReconnecting(false);
          setStatus('authenticated');
          scheduleRefresh();
          return;
        } catch (err) {
          if (cancelled) return;
          // A refusal — the credential is dead, or was never there. This is the
          // ordinary "you are signed out" path and it must stay instant.
          if (isCredentialRefusal(err) || attempt >= BOOT_RETRY_MS.length) {
            setReconnecting(false);
            setStatus('unauthenticated');
            return;
          }
          // Anything else is the server being unable to answer right now: a 429
          // from the per-IP auth limit, a restart, a flaky link. Bouncing to the
          // sign-in screen would be a lie about the session AND a trap — the
          // user re-enters credentials, which spends another request against the
          // same limit. Hold the splash, say why, and try again.
          setReconnecting(true);
          await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_MS[attempt]));
          if (cancelled) return;
        }
      }
    })();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [refreshUser, scheduleRefresh]);

  const role = user?.role ?? getClaims()?.role ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      role,
      can: (p: Permission) => status !== 'enrolling' && roleHas(role, p),
      isTranslateOnly:
        status !== 'enrolling' &&
        roleHas(role, 'content:translate') &&
        !roleHas(role, 'content:write'),
      reconnecting,
      setSession,
      logout,
      refreshUser,
      pendingDeepLink,
      setPendingDeepLink,
    }),
    [status, user, role, reconnecting, setSession, logout, refreshUser, pendingDeepLink],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
